import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { LOGO_URL, HERO_BG_URL } from "../lib/config";
import Countdown from "./Countdown";

export default function Hero({ settings }) {
  return (
    <section
      id="home"
      className="relative min-h-screen flex items-center overflow-hidden pt-24 pb-16"
      data-testid="hero-section"
    >
      {/* BG */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${HERO_BG_URL})` }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-fp-bg/70 via-fp-bg/60 to-fp-bg" />
      <div className="absolute inset-0 grid-bg opacity-40" />
      <div className="absolute inset-0 radial-glow" />

      <div className="relative max-w-7xl mx-auto px-5 md:px-8 w-full grid lg:grid-cols-[1.1fr_1fr] gap-10 lg:gap-16 items-center">
        {/* Text */}
        <div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-fp-cyan/30 bg-fp-cyan/5 backdrop-blur-sm"
          >
            <span className="w-2 h-2 rounded-full bg-fp-cyan animate-pulse" />
            <span className="text-fp-cyan text-xs font-heading font-semibold tracking-[0.3em] uppercase">
              4ª Edizione · 2026/2027
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mt-6 font-heading font-black text-5xl sm:text-6xl lg:text-7xl xl:text-8xl leading-[0.95] tracking-tight"
          >
            <span className="silver-gradient block">FANTA</span>
            <span className="cyan-gradient block cyan-text-glow">PARTENOPE</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35 }}
            className="mt-6 text-base md:text-lg text-white/70 max-w-xl leading-relaxed"
          >
            Il torneo di fantacalcio più elettrico d'<span className="electric-italia">Italia</span>. Montepremi da{" "}
            <span className="text-fp-cyan font-semibold">30.000€</span>, 9 leghe collaterali,
            una community che nel 2025/2026 ha superato i <span className="text-white font-semibold">919 partecipanti</span>.
          </motion.p>

          {settings?.countdown_enabled && settings?.countdown_target && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.45 }}
              className="mt-10"
            >
              <div className="text-[11px] tracking-[0.3em] uppercase text-white/50 mb-4 font-heading font-semibold">
                Inizio Torneo
              </div>
              <Countdown target={settings.countdown_target} enabled={settings.countdown_enabled} />
            </motion.div>
          )}

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.55 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            {settings && settings.registrations_open === false ? (
              <Link
                to="/register"
                className="group inline-flex items-center gap-3 px-7 py-4 rounded-full border border-white/20 bg-white/5 text-white/70 font-heading font-bold tracking-wider uppercase text-sm hover:border-fp-cyan/50 hover:text-fp-cyan transition-all duration-300"
                data-testid="hero-cta-register-closed"
              >
                Iscrizioni chiuse — scopri di più
                <span className="group-hover:translate-x-1 transition-transform">→</span>
              </Link>
            ) : (
              <Link
                to="/register"
                className="group inline-flex items-center gap-3 px-7 py-4 rounded-full bg-fp-cyan text-fp-bg font-heading font-bold tracking-wider uppercase text-sm hover:shadow-[0_0_30px_rgba(0,229,255,0.7)] hover:-translate-y-1 transition-all duration-300"
                data-testid="hero-cta-register"
              >
                Iscriviti ora — {settings?.registration_amount ? Math.round(settings.registration_amount) : 60}€
                <span className="group-hover:translate-x-1 transition-transform">→</span>
              </Link>
            )}
            <a
              href="#premi"
              className="inline-flex items-center gap-2 px-6 py-4 rounded-full border border-white/20 text-white hover:border-fp-cyan hover:text-fp-cyan font-heading font-semibold text-sm tracking-wider uppercase transition-all"
              data-testid="hero-view-prizes"
            >
              Scopri i premi
            </a>
          </motion.div>
        </div>

        {/* Logo */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.3, duration: 0.8 }}
          className="relative flex items-center justify-center"
        >
          <div className="absolute w-[80%] h-[80%] rounded-full bg-fp-cyan/20 blur-[80px]" />
          <img
            src={LOGO_URL}
            alt="Fanta Partenope"
            className="relative w-full max-w-md animate-float animate-pulse-glow"
            data-testid="hero-logo"
          />
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 hidden md:flex flex-col items-center gap-2 text-white/40">
        <span className="text-[10px] tracking-[0.3em] uppercase font-heading">Scroll</span>
        <div className="w-px h-10 bg-gradient-to-b from-fp-cyan to-transparent" />
      </div>
    </section>
  );
}
